import React, { useEffect, useRef } from 'react';
import { Segmented, Spin } from 'antd';
import MethodDistributionBar from './MethodDistributionBar';
import useDeckLearningMethod from '../../hooks/useDeckLearningMethod';
import useTranslation from '../../hooks/useTranslation';
import './LearningMethodPanel.css';

/**
 * Выбор методики колоды и картина колоды под ней.
 *
 * Один блок на все три типа практики, потому что вопрос у них один: какой политикой
 * учить эту колоду. Переключатель применяет выбор сразу — сервер пересчитывает сроки
 * одним проходом, и вернуться прежним кликом можно без потерь: прогресс живёт в
 * состоянии памяти, а методика только задаёт, о чём его спрашивать.
 *
 * Полоса и подписи под переключателем меняют смысл вместе с методикой, поэтому над
 * полосой всегда стоит подпись, что именно она считает. Полоса без такой подписи —
 * ровно тот случай, когда картинка выглядит знакомой и означает другое.
 */
export const METHODS = ['FIXED_SCHEDULE', 'ADAPTIVE_SCHEDULE', 'MASTERY_RULE'];

/**
 * Сколько подписей резервировать под полосой.
 *
 * Столбиков у методик разное число: у лестницы коробки плюс «не начато», у адаптивной
 * шесть корзин сроков плюс «не начато», у критерия — от нуля до «пять и больше». Если
 * оставлять под легенду ровно столько места, сколько занимают её подписи сейчас, панель
 * при каждом переключении меняет высоту, и всё, что под ней, прыгает. Поэтому место
 * держит невидимый двойник на семь подписей — по самой длинной картине.
 *
 * Берётся максимум с текущим числом столбиков: если на сервере настроят больше боксов,
 * двойник не окажется короче того, что рисуется.
 */
const RESERVED_LEGEND_ITEMS = 7;

/**
 * Ключ корзины «не начато». Она есть не у каждой методики, но имя у неё одно на все:
 * компонент полосы по нему выводит её из шкалы, а не красит самым тёмным оттенком.
 */
export const NOT_STARTED_BUCKET = 'new';

const DISTRIBUTION_TITLE = {
    FIXED_SCHEDULE: 'practice.method.distributionBox',
    ADAPTIVE_SCHEDULE: 'practice.method.distributionDue',
    MASTERY_RULE: 'practice.method.distributionCorrect',
};

const METHOD_LABEL = {
    FIXED_SCHEDULE: 'practice.method.fixedSchedule',
    ADAPTIVE_SCHEDULE: 'practice.method.adaptiveSchedule',
    MASTERY_RULE: 'practice.method.masteryRule',
};

const METHOD_HINT = {
    FIXED_SCHEDULE: 'practice.method.fixedScheduleHint',
    ADAPTIVE_SCHEDULE: 'practice.method.adaptiveScheduleHint',
    MASTERY_RULE: 'practice.method.masteryRuleHint',
};

const LearningMethodPanel = ({ deckId, practiceType, onMethodChange }) => {
    const { t } = useTranslation();
    const { stats, learningMethod, isLoading, isSwitching, switchTo } =
        useDeckLearningMethod(deckId, practiceType, t('practice.method.loadFailed'));

    // Страница держит методику в запросе на открытие практики, поэтому о ней нужно
    // сказать наружу — и при первой загрузке, и после переключения: иначе кнопка
    // «начать» откроет практику прежней политики, а полоса над ней будет показывать
    // новую. Ref, а не список зависимостей: родитель обычно передаёт стрелку прямо в
    // разметке, её тождество меняется на каждом рендере, и без него сообщение о смене
    // уходило бы бесконечно.
    const reported = useRef(null);
    useEffect(() => {
        // Ключ — колода и методика вместе. По одной методике две колоды с одинаковым
        // выбором не различались бы, и родитель не узнал бы ни о смене колоды, ни о её
        // размере. Пока выбор новой колоды не пришёл, методика докладывается пустой:
        // страница квиза иначе успевала бы открыть практику по выбору прошлой колоды.
        const key = `${deckId ?? ''}:${learningMethod ?? ''}`;
        if (reported.current !== key) {
            reported.current = key;
            // Вместе с методикой отдаём и картину: размер колоды нужен странице квиза,
            // а в ответе на открытие практики он есть не у всякой методики.
            onMethodChange?.(learningMethod, stats);
        }
    });

    const handleChange = async (next) => {
        if (next === learningMethod) {
            return;
        }
        await switchTo(next);
    };

    // Интервал повторения приписывается прямо к подписи коробки: цвет без срока
    // означает меньше, чем со сроком, а отдельная легенда рядом с этой же полосой
    // означала бы, что на странице два разных объяснения одних и тех же коробок.
    const daysByBox = stats?.reviewIntervalDays ? [0, ...stats.reviewIntervalDays] : null;

    const labelFor = (key) => {
        if (key.startsWith('box-')) {
            const box = Number(key.slice(4));
            const label = t('practice.box', { number: box + 1 });
            const days = daysByBox?.[box];
            if (days === undefined || days === null) {
                return label;
            }
            return `${label} · ${days === 0 ? t('practice.reviewNow') : t('practice.reviewInDays', { count: days })}`;
        }
        if (key.startsWith('correct-')) {
            const count = Number(key.slice(8));
            return count >= 5
                ? t('practice.method.correctPlus', { count })
                : t('practice.method.correctExactly', { count });
        }
        return t(`practice.method.due.${key}`);
    };

    return (
        <section className="methodPanel" aria-busy={isLoading || isSwitching}>
            <div className="methodPanel__head">
                <div className="methodPanel__title">{t('practice.method.title')}</div>
                <Segmented
                    className="methodPanel__switch"
                    disabled={isSwitching || isLoading}
                    // Пока выбор не пришёл, не выбрано ничего. Подставлять сюда лестницу
                    // значило бы утверждать неправду: у квизов по умолчанию критерий
                    // освоения, и первые полсекунды переключатель показывал бы методику,
                    // которой у колоды нет. Пустая строка не совпадает ни с одним
                    // вариантом, поэтому подсветки нет, а место остаётся занятым.
                    value={learningMethod ?? ''}
                    onChange={handleChange}
                    options={METHODS.map((method) => ({
                        label: t(METHOD_LABEL[method]),
                        value: method,
                    }))}
                />
            </div>

            {/* Все три подсказки лежат в одной ячейке, видна одна: высоту блока задаёт
                самая длинная из них, и она не меняется ни при переключении, ни пока
                методика ещё грузится. Подсказки разной длины — «критерий» в одну
                строку, «адаптивная» в две, — и без этого панель прыгала на каждый клик. */}
            <div className="methodPanel__hint">
                {METHODS.map((method) => (
                    <p
                        key={method}
                        className="methodPanel__hintText"
                        data-active={method === learningMethod}
                        aria-hidden={method !== learningMethod}
                    >
                        {t(METHOD_HINT[method])}
                    </p>
                ))}
            </div>

            <Spin spinning={isSwitching} size="small">
                <div className="methodPanel__stats">
                    <div className="methodPanel__statsTitle">
                        {t(DISTRIBUTION_TITLE[learningMethod] ?? DISTRIBUTION_TITLE.FIXED_SCHEDULE)}
                    </div>
                    {/* Пока сводка не пришла, здесь пусто: место под картину держится
                        только тогда, когда картина будет — иначе панель стояла бы с
                        пустым полем под одной строкой текста. */}
                    {stats && (
                        <div className="methodPanel__picture">
                            <div className="methodPanel__pictureReal">
                                <MethodDistributionBar
                                    buckets={stats.buckets}
                                    labelFor={labelFor}
                                    emptyLabel={t('practice.notStarted')}
                                    notStartedKey={NOT_STARTED_BUCKET}
                                />
                            </div>
                            {/* Двойник держит высоту: у него столько же подписей, сколько
                                у самой длинной картины, и та же сетка. Во время переключения
                                столбиков нет вовсе — без него блок схлопывался бы и снова
                                разворачивался на каждый клик. */}
                            <div className="methodPanel__pictureGhost" aria-hidden="true" data-testid="method-picture-ghost">
                                <div className="methodBar" />
                                <div className="methodLegend">
                                    {Array.from(
                                        { length: Math.max(RESERVED_LEGEND_ITEMS, stats.buckets?.length ?? 0) },
                                        (unused, index) => (
                                            <span className="methodLegend__item" key={index} data-testid="method-ghost-legend-item">
                                                <span className="methodLegend__swatch" />
                                                <span className="methodLegend__label">&nbsp;</span>
                                            </span>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Spin>

            {/* Списка элементов здесь нет: он живёт на странице прогресса (/progress),
                со своей сортировкой и фильтрами. Панель — картина и переключатель. */}
        </section>
    );
};

export default LearningMethodPanel;
